/**
 * What Home reads besides the studio (which already holds the brand, its changes and its items):
 * every item with its document, the problems that stop the next publish, the uploads and storage,
 * and the team's activity. One read when Home opens; a failed part is empty.
 */

import { useEffect, useState } from "react";
import { entities, media, people, publishing, type AuditEntry, type Budget, type Entity, type MediaAsset, type Problem } from "@/api/admin";
import type { AdminClient } from "@/api/client";

export interface HomeData {
  full: Entity[];
  /** What stops the next publish. */
  errors: Problem[];
  assets: MediaAsset[];
  budget: Budget | null;
  audit: AuditEntry[];
}

export function useHomeData(client: AdminClient): HomeData | null {
  const [data, setData] = useState<HomeData | null>(null);
  useEffect(() => {
    let live = true;
    void Promise.allSettled([entities.listFull(client), publishing.validate(client), media.list(client), media.budget(client), people.audit(client)])
      .then(([full, check, assets, budget, audit]) => {
        if (!live) return;
        setData({
          full: full.status === "fulfilled" ? full.value.entities : [],
          errors: check.status === "fulfilled" ? check.value.problems.filter((p) => p.level === "error") : [],
          assets: assets.status === "fulfilled" ? assets.value.assets : [],
          budget: budget.status === "fulfilled" ? budget.value : null,
          audit: audit.status === "fulfilled" ? audit.value.entries : [],
        });
      });
    return () => { live = false; };
  }, [client]);
  return data;
}
